import React, { ReactNode } from 'react';
import {
  Modal,
  View,
  ScrollView,
  StyleSheet,
  Platform,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { isWeb } from '../../utils/platformUtils';
import { useTheme } from '../../context/ThemeContext';
import { createThemedStyles } from '../../theme/useThemedStyles';
import KeyboardDismissToolbar from '../KeyboardDismissToolbar';

export const MODAL_WIDTH = {
  sm: 400,
  md: 520,
  lg: 640,
} as const;

export type ModalSize = keyof typeof MODAL_WIDTH;

type AppModalProps = {
  visible: boolean;
  onRequestClose: () => void;
  children: ReactNode;
  size?: ModalSize;
  animationType?: 'none' | 'slide' | 'fade';
  scrollable?: boolean;
  showCloseButton?: boolean;
};

/** Centered, max-width modal shell for web; unchanged behavior on native. */
const AppModal = ({
  visible,
  onRequestClose,
  children,
  size = 'md',
  animationType = 'fade',
  scrollable = true,
  showCloseButton = true,
}: AppModalProps) => {
  const styles = useAppModalStyles();
  const { colors } = useTheme();
  const maxWidth = isWeb ? MODAL_WIDTH[size] : undefined;

  const body = scrollable ? (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.scrollContent}
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'none'}
      showsVerticalScrollIndicator
    >
      {children}
    </ScrollView>
  ) : (
    <View style={styles.scrollContent}>{children}</View>
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType={isWeb ? 'fade' : animationType}
      onRequestClose={onRequestClose}
    >
      <View style={styles.modalRoot}>
        <View style={styles.overlay}>
          <View style={[styles.box, maxWidth != null && { maxWidth }]}>
            {isWeb && showCloseButton ? (
              <TouchableOpacity
                style={styles.closeBtn}
                onPress={onRequestClose}
                accessibilityRole="button"
                accessibilityLabel="Close"
              >
                <Ionicons name="close" size={22} color={colors.grey[600]} />
              </TouchableOpacity>
            ) : null}
            {body}
          </View>
        </View>
        {Platform.OS === 'ios' ? <KeyboardDismissToolbar /> : null}
      </View>
    </Modal>
  );
};

type GridProps = { children: ReactNode; style?: ViewStyle };

/** Two-column form layout on web only. */
export const ModalFormGrid = ({ children, style }: GridProps) => {
  if (!isWeb) {
    return <>{children}</>;
  }
  return <View style={[gridStyles.grid, style]}>{children}</View>;
};

export const ModalFormField = ({ children, fullWidth }: { children: ReactNode; fullWidth?: boolean }) => {
  if (!isWeb) {
    return <>{children}</>;
  }
  return <View style={[gridStyles.field, fullWidth && gridStyles.fieldFull]}>{children}</View>;
};

const gridStyles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 8,
  },
  field: {
    width: 'calc(50% - 8px)' as unknown as number,
    minWidth: 200,
    flexGrow: 1,
    flexShrink: 0,
  },
  fieldFull: {
    width: '100%',
  },
});

const useAppModalStyles = createThemedStyles((colors) => ({
  modalRoot: {
    flex: 1,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: isWeb ? 32 : 16,
  },
  box: {
    backgroundColor: colors.surface,
    borderRadius: 16,
    width: isWeb ? ('100%' as const) : ('92%' as const),
    maxHeight: isWeb ? ('min(90vh, 800px)' as unknown as number) : ('90%' as const),
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }
      : {
          elevation: 8,
        }),
  },
  scroll: {
    flexGrow: 0,
  },
  scrollContent: {
    padding: 24,
    paddingTop: isWeb ? 44 : 24,
    paddingBottom: isWeb ? 28 : 24,
  },
  closeBtn: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 10,
    padding: 6,
    borderRadius: 20,
    backgroundColor: colors.grey[100],
  },
}));

export default AppModal;
